import { Utils, ELanguage } from '@microfrontends/react-components'
import dateFormat from 'dateformat'
import { RefObject } from 'react'
import { IJobInfo } from '../components/JobItem/type'
import viTrans from '../resources/translations/vi.json'
import enTrans from './../resources/translations/en.json'
import { convertISOToMillisecond } from './dateTime'
import { LOGIN_POPUP } from '../components/constants'
import { TAuthUser } from '../services/userService'

export const assetUrl = (path: string): string => {
    return process.env.NEXT_PUBLIC_APP_ASSET_URL + path
}

export const imageServerUrl = (path: string): string => {
    return process.env.NEXT_PUBLIC_IMAGE_URL + path
}

export function translate(key: string, langCode: ELanguage) {
    if (langCode === ELanguage.EN) {
        // @ts-ignore
        return enTrans[key] || key
    }
    // @ts-ignore
    if (!viTrans[key]) console.log(`${langCode} | --- ${key} --- translation is missing`)
    // @ts-ignore
    return viTrans[key] || key
}

export function createTFunction(langCode: ELanguage) {
    return (key: string) => {
        return translate(key, langCode)
    }
}

export function removeScriptTag(s: string) {
    if (typeof document === 'undefined') {
        return s
    }

    const div = document.createElement('div')
    div.innerHTML = s

    const scripts = div.getElementsByTagName('script')

    let i = scripts.length
    while (i--) {
        scripts[i].parentNode?.removeChild(scripts[i])
    }

    return div.innerHTML
}

export function handleSuccessStatus(langCode: ELanguage, text: string) {
    Utils.showToast(langCode === ELanguage.EN ? 'Success' : 'Thành Công', text)
}

export function handleFailedStatus(langCode: ELanguage, text: string) {
    Utils.showToast(langCode === ELanguage.EN ? 'Error' : 'Lỗi', text, 'error')
}

export function scrollToRef(ref: RefObject<HTMLElement>) {
    if (typeof window === 'undefined' || typeof document === 'undefined' || !ref) {
        return
    }

    const elOffsetTop = ref.current?.getBoundingClientRect().top || 0
    const windowOffsetTop = document.documentElement.scrollTop
    window.scrollTo({
        top: elOffsetTop + windowOffsetTop - 90, // 90 = 74 (fixed header height) + 16 (padding)
        behavior: 'smooth'
    })
}

export const scrollContainerTo = (containerSelector: string, delay: number = 0, top: number = 0, left: number = 0) => {
    if (!Utils.isDesktop()) {
        setTimeout(() => {
            const scrollContainer = document.querySelector(containerSelector)
            scrollContainer?.scroll({ top, left })
        }, delay)
    }
}

export const handleFocusOnFirstErrorField = (container: string | HTMLElement) => {
    if (typeof document !== 'undefined' && container) {
        const inputErrorSelector: string = `[class*="input-module_inputError"]`
        const benefitErrorSelector: string = `.benefit-has-error .tag-item:first-child`

        // Set timeout to make sure that all states are set and re-rendering have been made
        setTimeout(() => {
            const containerElement = typeof container === 'string' ? document.querySelector(container) : container
            const firstErrorField = containerElement?.querySelectorAll(
                `${inputErrorSelector}, ${benefitErrorSelector}`
            )[0] as HTMLInputElement | null

            if (firstErrorField) {
                firstErrorField.focus()
            }
        }, 150)
    }
}

export function removeRedundantSpaces(str: string) {
    return str.replace(/\s+/g, ' ')
}

export const formatSalary = (item: IJobInfo, language: ELanguage) => {
    let salaryText = ''
    if (item.isSalaryVisible) {
        if (item.salaryMin === 0 && item.salaryMax > 0)
            salaryText = (language === ELanguage.EN ? 'Up to' : 'Tới') + ' $' + item.salaryMax
        else if (item.salaryMin > 0 && item.salaryMax === 0)
            salaryText = (language === ELanguage.EN ? 'From' : 'Từ') + ' $' + item.salaryMin
        else if (item.salaryMin > 0 && item.salaryMax > 0) salaryText = '$' + item.salaryMin + ' - $' + item.salaryMax
        else salaryText = language === ELanguage.EN ? 'Negotiable' : 'Thương lượng'
    } else salaryText = language === ELanguage.EN ? 'Negotiable' : 'Thương lượng'
    return salaryText
}

export const formatYoututeUrl = (url: string): string => {
    return url.replace('watch?v=', 'embed/')
}

export function buildQueryString(params: any) {
    let queryString: any[] = []

    for (var key in params) {
        if (params.hasOwnProperty(key) && !!params[key]) {
            queryString.push(`${key}=${encodeURIComponent(params[key])}`)
        }
    }

    return queryString.length > 0 ? queryString.join('&') : ''
}

export function checkIsNew(item: IJobInfo) {
    const onlineDate = new Date(convertISOToMillisecond(item.onlineOn) * 1000)
    const now = new Date()
    let isNew = false
    try {
        isNew = dateFormat(onlineDate, 'yyyy/mm/dd') === dateFormat(now, 'yyyy/mm/dd')
    } catch {
        isNew = false
    }
    return isNew
}

export function getPathFromUrl(url: string) {
    return url.split(/[?#]/)[0]
}

export function buildCompanyUrl(companyId: number, companySlug: string, langCode: ELanguage) {
    const prefixPath = langCode === ELanguage.EN ? 'companies' : 'nha-tuyen-dung'
    return `/${prefixPath}/${companySlug}-c${companyId}`
}

export function transformToLatin(text: string) {
    text = text.toString().toLowerCase().trim()

    const sets = [
        { to: 'a', from: '[ÀÁÂÃÄÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶἀ]' },
        { to: 'c', from: '[ÇĆĈČ]' },
        { to: 'd', from: '[ÐĎĐÞ]' },
        { to: 'e', from: '[ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆ]' },
        { to: 'g', from: '[ĜĞĢǴ]' },
        { to: 'h', from: '[ĤḦ]' },
        { to: 'i', from: '[ÌÍÎÏĨĪĮİỈỊ]' },
        { to: 'j', from: '[Ĵ]' },
        { to: 'ij', from: '[Ĳ]' },
        { to: 'k', from: '[Ķ]' },
        { to: 'l', from: '[ĹĻĽŁ]' },
        { to: 'm', from: '[Ḿ]' },
        { to: 'n', from: '[ÑŃŅŇ]' },
        { to: 'o', from: '[ÒÓÔÕÖØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠ]' },
        { to: 'oe', from: '[Œ]' },
        { to: 'p', from: '[ṕ]' },
        { to: 'r', from: '[ŔŖŘ]' },
        { to: 's', from: '[ßŚŜŞŠȘ]' },
        { to: 't', from: '[ŢŤ]' },
        { to: 'u', from: '[ÙÚÛÜŨŪŬŮŰŲỤỦỨỪỬỮỰƯ]' },
        { to: 'w', from: '[ẂŴẀẄ]' },
        { to: 'x', from: '[ẍ]' },
        { to: 'y', from: '[ÝŶŸỲỴỶỸ]' },
        { to: 'z', from: '[ŹŻŽ]' },
        { to: '-', from: "[·/_,:;']" }
    ]

    sets.forEach((set) => {
        text = text.replace(new RegExp(set.from, 'gi'), set.to)
    })

    return text
}

export function slugify(slug: string) {
    slug = slug.replace(/^\s+|\s+$/g, '') // trim
    slug = slug.toLowerCase()

    // remove accents
    slug = transformToLatin(slug)

    slug = slug
        .replace(/[^a-z0-9 -]/g, '-') // invalid chars to -
        .replace(/\s+/g, '-') // collapse whitespace and replace by -
        .replace(/-+/g, '-') // collapse dashes

    return slug
}

function escapeRegExp(string: string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export function replaceAll(str: string, find: string, replace: string) {
    return str.replace(new RegExp(escapeRegExp(find), 'g'), replace)
}

export function showLoginModal(langCode: ELanguage, callbackFunction?: (user: TAuthUser) => void) {
    if (typeof window === 'undefined') {
        return
    }
    const loginEvent = new CustomEvent(LOGIN_POPUP, {
        detail: {
            langCode: langCode === ELanguage.VI ? 'vi' : 'en',
            isNeedReload: false,
            trackingProperties: {
                pageName: 'company_profile'
            },
            onLoggedIn: (user: TAuthUser) => {
                callbackFunction && callbackFunction(user)
            }
        }
    })
    window.dispatchEvent(loginEvent)
}

export function stripHtml(html: string) {
    if (typeof window === 'undefined') return

    let tmp = document.createElement('DIV')
    tmp.innerHTML = html

    return tmp.textContent || tmp.innerText || ''
}
